import React, { useEffect, useState, memo } from 'react';
import { ZapIcon, DownloadIcon, SmartphoneIcon, BrainIcon, GlobeIcon, AlertCircleIcon } from 'lucide-react';
import { CountdownBadge } from './CountdownBadge';
import { LiveVisitorCounter } from './LiveVisitorCounter';
// Memoize the pricing card to prevent unnecessary re-renders
const PricingCard = memo(({
  showArgentinePrice,
  isLoading,
  handleBuyClick
}) => <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all hover:scale-105 duration-300" data-id="element-152">
      <div className="px-6 py-8 bg-green-700 sm:p-10 sm:pb-6" data-id="element-153">
        <div className="flex justify-between items-center" data-id="element-154">
          <h3 className="text-2xl leading-8 font-extrabold text-white tracking-tight" data-id="element-155">
            KIT DE EMERGENCIA ANTI-ESTRÉS
          </h3>
          <div className="ml-4 bg-green-900 rounded-full px-3 py-1 text-xs font-semibold text-white uppercase tracking-wide" data-id="element-156">
            Acceso completo al sistema
          </div>
        </div>
      </div>
      <div className="border-t border-green-100 px-6 py-6 sm:px-10" data-id="element-157">
        <div className="flex items-center justify-center" data-id="element-158">
          <div className="text-center" data-id="element-159">
            {isLoading ? <div className="text-xl font-medium text-gray-500" data-id="element-160">
                Cargando precios...
              </div> : showArgentinePrice ? <>
                <div className="flex items-baseline justify-center mb-2" data-id="element-161">
                  <span className="line-through text-gray-500 text-lg" data-id="element-162">
                    ARS $ 54.800
                  </span>
                </div>
                <div className="flex items-baseline justify-center" data-id="element-163">
                  <span className="text-5xl font-extrabold text-gray-900" data-id="element-164">
                    ARS $ 19.997
                  </span>
                </div>
                <p className="text-sm text-green-600 font-medium mt-1" data-id="element-165">
                  ¡Ahorro de $34.803!
                </p>
              </> : <>
                <div className="flex items-baseline justify-center mb-2" data-id="element-166">
                  <span className="line-through text-gray-500 text-lg" data-id="element-167">
                    $54 USD
                  </span>
                </div>
                <div className="flex items-baseline justify-center" data-id="element-168">
                  <span className="text-5xl font-extrabold text-gray-900" data-id="element-169">
                    $19 USD
                  </span>
                </div>
                <p className="text-sm text-green-600 font-medium mt-1" data-id="element-170">
                  ¡Ahorro de $35 USD!
                </p>
              </>}
          </div>
        </div>
        <div className="mt-4 text-center" data-id="element-171">
          <p className="text-sm text-gray-500" data-id="element-172">
            ¡Oferta por tiempo limitado! Después de 200 copias vendidas, el
            precio volverá a {showArgentinePrice ? 'ARS $ 54.800' : '$54 USD'}
          </p>
        </div>
        <ul className="mt-8 space-y-4" data-id="element-173">
          <li className="flex items-start" data-id="element-174">
            <div className="flex-shrink-0" data-id="element-175">
              <DownloadIcon className="h-6 w-6 text-green-500" aria-hidden="true" data-id="element-176" />
            </div>
            <p className="ml-3 text-base text-gray-700" data-id="element-177">Descarga inmediata</p>
          </li>
          <li className="flex items-start" data-id="element-178">
            <div className="flex-shrink-0" data-id="element-179">
              <SmartphoneIcon className="h-6 w-6 text-green-500" aria-hidden="true" data-id="element-180" />
            </div>
            <p className="ml-3 text-base text-gray-700" data-id="element-181">
              Acceso desde cualquier dispositivo
            </p>
          </li>
          <li className="flex items-start" data-id="element-182">
            <div className="flex-shrink-0" data-id="element-183">
              <BrainIcon className="h-6 w-6 text-green-500" aria-hidden="true" data-id="element-184" />
            </div>
            <p className="ml-3 text-base text-gray-700" data-id="element-185">
              Mejora desde la primera sesión
            </p>
          </li>
        </ul>
        <div className="mt-8" data-id="element-186">
          <a href="https://clickylee.com/products/kit-de-emergencias-anti-stress-5-bonos-gratis?variant=46954999415019" onClick={handleBuyClick} className="w-full flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors" data-id="element-187">
            Descargar Ahora El Kit Anti-Estrés
          </a>
          <p className="text-xs text-center text-gray-500 mt-2" data-id="element-188">
            Pago único. Sin suscripciones ni cargos ocultos.
          </p>
        </div>
      </div>
      <div className="px-6 py-8 bg-gray-50 text-center" data-id="element-189">
        <p className="text-lg font-medium text-gray-900" data-id="element-190">
          No se trata de soñar con estar tranquilo.
        </p>
        <p className="mt-2 text-base text-gray-500" data-id="element-191">
          Se trata de tener las herramientas para lograrlo.
        </p>
      </div>
    </div>);
export function PricingSection() {
  const [showArgentinePrice, setShowArgentinePrice] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    // Check if user is in Argentina using browser language as initial guess
    const isArgentineBrowser = navigator.language === 'es-AR';
    // Set initial state based on browser language
    setShowArgentinePrice(isArgentineBrowser);
    setIsLoading(false); // Set loading to false immediately to show default prices
    // Use a less blocking approach for geolocation - after initial render
    let isMounted = true;
    // Defer the API call to not block initial page load
    const timeoutId = setTimeout(() => {
      fetch('https://ipapi.co/json/').then(response => response.json()).then(data => {
        // Only update state if component is still mounted
        if (isMounted) {
          // If the country code is AR (Argentina), show Argentine prices
          const isArgentinaByIP = data.country_code === 'AR';
          setShowArgentinePrice(isArgentinaByIP);
        }
      }).catch(error => {
        console.error('Error fetching location data:', error);
        // Fallback already set, no need to update
      }).finally(() => {
        if (isMounted) setIsLoading(false);
      });
    }, 1000); // Delay API call by 1 second
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, []);
  const handleBuyClick = () => {
    // Track Add to Cart event with Meta Pixel
    if (window.fbq) {
      window.fbq('track', 'AddToCart');
    }
  };
  return <section className="py-16 bg-gray-50" data-id="element-192">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-193">
        <div className="text-center mb-12" data-id="element-194">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl" data-id="element-195">
            <span className="block" data-id="element-196">Lanzamiento exclusivo</span>
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-197">
            Por tiempo limitado puedes acceder al Kit de Emergencia Anti-Estrés
            por una fracción de su valor real.
          </p>
        </div>
        <div className="max-w-lg mx-auto" data-id="element-198">
          <PricingCard showArgentinePrice={showArgentinePrice} isLoading={isLoading} handleBuyClick={handleBuyClick} data-id="element-199" />
          <div className="mt-4 text-center" data-id="element-200">
            <button onClick={() => setShowArgentinePrice(!showArgentinePrice)} className="flex items-center text-sm text-gray-600 hover:text-gray-800 bg-gray-50 px-3 py-1 rounded-full transition-colors" data-id="element-201">
              {showArgentinePrice ? 'Cambiar a USD' : 'Cambiar a ARS'}
            </button>
          </div>
        </div>
      </div>
    </section>;
}